import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";

// Callers pass a resolved `{ startAt, endAt }` from
// `@shared/v2/DateRangeDropdown`'s `resolveDateRange`. When endpoints are
// missing, omit the range params so the backend applies its default.
const getDateRange = (dateRange) => {
  if (dateRange?.startAt && dateRange?.endAt) {
    return {
      startDate: dateRange.startAt,
      endDate: dateRange.endAt,
    };
  }
  return {};
};

const transformData = (filters, rawData) => {
  const filterKeys = Object.keys(filters).filter((key) => filters[key]);
  const dataKeys = Object.keys(rawData.buyer);
  const transformedData = dataKeys.reduce((acc, key) => {
    acc[key] = {
      actual: filterKeys.reduce((sum, filterKey) => sum + (rawData[filterKey][key]?.actual || 0), 0),
      goal: Number(Math.round(rawData[filterKeys[0]]?.[key]?.goal) || 0),
      onTarget: Number(rawData[filterKeys[0]]?.[key]?.onTarget || 0),
    };
    return acc;
  }, {});
  transformedData.callsContactedRatio = Math.round(
    (transformedData.contacted.actual / (transformedData.calls.actual || 1)) * 100,
  );
  transformedData.contactedApptSetRatio = Math.round(
    (transformedData.apptSet.actual / (transformedData.contacted.actual || 1)) * 100,
  );
  transformedData.apptSetApptHeldRatio = Math.round(
    (transformedData.apptHeld.actual / (transformedData.apptSet.actual || 1)) * 100,
  );
  transformedData.apptHeldApptSignedRatio = Math.round(
    (transformedData.apptSigned.actual / (transformedData.apptHeld.actual || 1)) * 100,
  );
  transformedData.apptSignedPendedRatio = Math.round(
    (transformedData.pended.actual / (transformedData.apptSigned.actual || 1)) * 100,
  );
  transformedData.pendedClosedRatio = Math.round(
    (transformedData.closed.actual / (transformedData.pended.actual || 1)) * 100,
  );

  return transformedData;
};

const useSalesFunnel = ({
  agentUuid = null,
  dateRange = "",
  filters = { buyer: true, seller: true, buyerSeller: true, na: true },
  perAgent = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState(null);
  const [error, setError] = useState(null);

  const data = useMemo(() => {
    if (!rawData) {
      if (perAgent) {
        return [];
      }
      return {
        callsContactedRatio: 0,
        contactedApptSetRatio: 0,
        apptSetApptHeldRatio: 0,
        apptHeldApptSignedRatio: 0,
        apptSignedPendedRatio: 0,
        pendedClosedRatio: 0,
      };
    }

    if (perAgent) {
      return rawData.map((agent) => ({
        ...agent,
        name: `${agent.firstName} ${agent.lastName}`.trim(),
        data: transformData(filters, agent.data),
      }));
    }
    return transformData(filters, rawData);
  }, [filters, rawData]);

  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    caseTransformingAxios
      .get(`/api/v4/reporting/${agentUuid ? "agent" : "business"}_dashboard`, {
        signal: abortController.signal,
        params: {
          personUuid: agentUuid,
          section: `sales_funnel${perAgent ? "s" : ""}`,
          ...getDateRange(dateRange),
        },
      })
      .then((res) => {
        setRawData(perAgent ? res.data.report : res.data);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setError(err.response.data?.message || "Whoops! Something went wrong.");
          setLoading(false);
        }
      });
    return () => abortController.abort();
  }, [agentUuid, dateRange]);

  return { data, error, loading };
};

export default useSalesFunnel;
